import { useEffect, useRef } from 'react'
import { matchRoutes, useLocation } from 'react-router-dom'

import { trackPage } from 'torqcloud-ui'
import type { AnalyticsPage } from 'torqcloud-ui'

import routes from '@/routes'

// Shape of the per-route `handle` object we read to resolve a page name. Routes
// opt into centralized page-view tracking by adding `pageName` to their handle
// (see routes.tsx). Routes without a pageName do not emit a page view here so
// that a handful of call sites can keep firing custom-property page views
// themselves (for example Organization Settings, which reports the active tab).
interface PageTrackingRouteHandle {
  pageName?: AnalyticsPage
}

// Walk from the deepest matched route outward and return the first declared
// page name, so a leaf route can override a parent layout route.
function resolvePageName(matches: ReturnType<typeof matchRoutes>): AnalyticsPage | undefined {
  if (!matches) return undefined
  for (let index = matches.length - 1; index >= 0; index--) {
    const handle = matches[index].route.handle as PageTrackingRouteHandle | undefined
    if (handle?.pageName) return handle.pageName
  }
  return undefined
}

// Centralized route-based page-view tracking. Fires trackPage(routeName, {
// path, previousPath }) on every location change for routes that declare a
// pageName. Wire this once in the app shell (AppLayout) so every route emits a
// page view without a hand-written call at each page. organizationId, userId,
// and plan are attached centrally by the analytics wrapper, so they are not
// passed here.
//
// This resolves the active route with matchRoutes(routes, location) rather than
// useMatches(), because useMatches() only works under a data router
// (createBrowserRouter/RouterProvider) and this app uses a classic
// <BrowserRouter> + useRoutes(routes), under which useMatches() throws. This
// mirrors the existing matchRoutes pattern in TitleManager.
export function usePageTracking(): void {
  const location = useLocation()
  const matches = matchRoutes(routes, location)
  const previousPathRef = useRef<string | null>(null)
  const lastPageNameRef = useRef<AnalyticsPage | null>(null)

  const pageName = resolvePageName(matches)
  const path = `${location.pathname}${location.search}`

  useEffect(() => {
    const previousPath = previousPathRef.current
    previousPathRef.current = path

    if (!pageName) return

    // A single visit can land on two paths when a page redirects to a canonical
    // sibling (for example /teams/:id/run -> /teams/:id/runs/:runId). Both legs
    // resolve to the same page name, so emitting on each one would double-count
    // the visit. Skip a page name that repeats the one we emitted last.
    if (lastPageNameRef.current === pageName) return
    lastPageNameRef.current = pageName

    trackPage(pageName, previousPath ? { path, previousPath } : { path })
  }, [path, pageName])
}
